// Zeichnet das Ufer (§6) auf ein Canvas: seit Phase 3 Kreise, seit der Phase Kern Figuren (figures.ts), mit Taste G umschaltbar.
// Liest nur, was Sim und Inhalte liefern; schreibt nirgends hinein.
import { combatRules } from '../content/index.ts';
import { FrameFlag, type BattleEvent } from '../sim/index.ts';
import { hasKniffArt, sprite, type GlowSpec } from './art.ts';
import {
  drawFigure,
  drawFigureGround,
  drawFigureStatus,
  drawFigureVeil,
  drawFx,
  drawMotionLines,
  drawPoisonSign,
  drawSigns,
  figureGeo,
  traitSpots,
  type FigureGeo,
} from './figures.ts';
import type { Pose } from './shore/motion.ts';
import { calmBar, calmLayout, drawCalmBar, drawSmallClass, drawSmallStatus, drawThreatArcs, hasSmallSigns, type CalmLayout } from './calm.ts';
import type { ActiveFx } from './shore/signs.ts';

export type DrawUnit = {
  uid: number;
  side: 'friend' | 'enemy';
  kind: 'friend' | 'stormling' | 'infested' | 'tree';
  name: string;
  short: string;
  color: string;
  radius: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  mut: number;
  flags: number;
  /** K46: die zweite Bitmaske (FrameFlag2), nur im Übungsufer */
  flags2?: number;
  traits: string[];
  /** Nur zum Betrachten: wer schon weg ist und warum (Abschied, vertrieben, entkommen, gefällt) */
  goneLabel?: string;
  /** T12 Geister-Leben: so viel Leben hatte er eben noch; der Unterschied leuchtet kurz hell nach */
  ghostHp?: number;
  /** Fabi (Phase Kern): Wort über der Figur je Zustand nach seiner Herkunft, etwa „Heißhunger“ statt „Bau“ */
  statusWords?: Partial<Record<number, string>>;
  /** T12 Treffer-Wucht: 0 bis 1, wie heftig er gerade getroffen wurde */
  hit?: number;
  /** F32 (Fabi): Giftstapel, sichtbar, damit das Überspringen Spaß macht */
  poison?: number;
  /** K6: so viel Mut braucht er für seinen Kniff */
  mutMax?: number;
  /** Runde 8 (Fabi): Schild am Lebensbalken, auch bei vollem Leben */
  shield?: number;
  /** F42 (Fabi, Phase Kern): verwundet; fällt er noch einmal, nimmt er Abschied */
  wounded?: boolean;
  /** K35 (Fabi, Phase Kern): so viele Sturmwesen greifen ihn an (beim Aufstellen: zuerst), als rote Kerben */
  attackers?: number;
  /** K45 (Fabi, 17.10.2026 nachts): von wo sie kommen (Feldeinheiten), als rote Spitzen am Bodenring */
  attackerFrom?: { x: number; y: number }[];
  /** Phase Kern (T21): welche Figur aus public/art/ (Spezies oder Sturmwesen); ohne Bild bleibt der Kreis */
  art?: string;
  /** T21: Klasse für Ring und Zeichen, auch bei Befallenen */
  friendClass?: string;
  /** K38: Eigenschaften als IDs, für ihre Zeichen */
  traitIds?: string[];
  /** T31, K39: Bewegung im Moment (Stehen, Laufen, Angriff, Treffer, Sprung, Abschied) */
  pose?: Pose;
  /** S10: Schützling einer Leibwache (im Kampf) */
  warded?: boolean;
  /** S13 Pfote halten (Runde 7): hält gerade die Pfote eines Freundes oder wird gehalten */
  pawHeld?: boolean;
  /** Version 3 des Manifests: Bilder für Kniffe und Eigenschaften an der Figur (T31, K38) */
  fx?: ActiveFx[];
  /** Schein um die Figur (Heißhunger, Hautgift, Stärkung) */
  glow?: GlowSpec;
  /** K38 Feige auf der Flucht: wackelt um einen Bildpixel */
  wobble?: number;
  /** G30 (Fabi, Phase Kern): Spezies eines Sturmwesens; der Name ist nur der Spitzname, die Spezies steht beim Darüberfahren */
  kindName?: string;
  /** T37 (Fabi): Aussehen innerhalb der Spezies, 0 wie gezeichnet; zwei Freunde derselben Spezies sehen nie gleich aus */
  look?: number;
};

/** Schriften auf dem Feld; das Übungsufer setzt Zweigbuch (T38) */
export const FONT = { body: 'system-ui, sans-serif', title: 'Georgia, serif' };

export type Cursor = { x: number; y: number; holding: string | null; visible: boolean };

const FIELD = combatRules.field;

/**
 * Farben je Klasse (F4). Im UI steht nur der Klassenname, die Farbe hilft beim Wiedererkennen. T21 (Phase Kern): wie die
 * Zeichen des Grafik-Leads in public/art/manifest.json, ohne Gelb und Violett (die gehören den Kniffen).
 */
export const CLASS_COLORS: Record<string, string> = {
  dickfell: '#9a6a40',
  raufbold: '#d0463a',
  springinsfeld: '#e8862a',
  adlerauge: '#5aa8e0',
  giftzahn: '#7ac23a',
  beschuetzer: '#e88aa8',
};
export const ENEMY_COLOR = '#6b6a8f';
export const TREE_COLOR = '#4f7a4a';

const C = {
  ground: '#2f3d36',
  groundFar: '#29342f',
  friendZone: 'rgba(246, 199, 107, 0.07)',
  enemyZone: 'rgba(107, 106, 143, 0.10)',
  line: 'rgba(243, 233, 210, 0.18)',
  cream: '#f3e9d2',
  hp: '#9fd18b',
  hpLow: '#e0795f',
  hpBack: 'rgba(0,0,0,0.45)',
  mut: '#f6c76b',
  enemyKniff: '#c99cff',
  damage: '#ffd7c2',
  crit: '#ffb347',
  heal: '#a8f0a0',
  mark: '#ff6b5a',
  shadow: 'rgba(10, 12, 20, 0.55)',
  lightning: '#fff7c2',
};

export function setupCanvas(canvas: HTMLCanvasElement): { ctx: CanvasRenderingContext2D; scale: number } {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const w = Math.max(1, Math.round(rect.width * dpr));
  const h = Math.max(1, Math.round(rect.height * dpr));
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d')!;
  return { ctx, scale: w / FIELD.width };
}

/** R25 (Fabi, Runde 7): Je näher das Unwetter, desto dunkler das Ufer */
export const STORM_TINT: Record<string, number> = { ruhig: 0, aufziehend: 0.05, wetterleuchten: 0.1, nah: 0.16, dicht: 0.22, eingeholt: 0.3 };
/** und desto mehr Regen fällt */
const STORM_RAIN: Record<string, number> = { ruhig: 0, aufziehend: 0, wetterleuchten: 0, nah: 45, dicht: 100, eingeholt: 150 };

/** base (Übungsufer): zeichnet ein Bild als Boden und gibt true zurück; sonst der Verlauf */
export function drawGround(ctx: CanvasRenderingContext2D, s: number, showZones: boolean, rain: number, stage = 'ruhig', base?: () => boolean): void {
  const w = FIELD.width * s;
  const h = FIELD.height * s;
  if (!base?.()) {
    const g = ctx.createLinearGradient(0, 0, w, 0);
    g.addColorStop(0, C.ground);
    g.addColorStop(1, C.groundFar);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  }
  if (showZones) {
    const fz = FIELD.friendZone;
    const ez = FIELD.enemyZone;
    ctx.fillStyle = C.friendZone;
    ctx.fillRect(fz.xMin * s, 0, (fz.xMax - fz.xMin) * s, h);
    ctx.fillStyle = C.enemyZone;
    ctx.fillRect(ez.xMin * s, 0, (ez.xMax - ez.xMin) * s, h);
    ctx.strokeStyle = C.line;
    ctx.setLineDash([6 * (s / 8), 6 * (s / 8)]);
    ctx.lineWidth = Math.max(1, s / 8);
    ctx.beginPath();
    ctx.moveTo(((fz.xMax + ez.xMin) / 2) * s, 0);
    ctx.lineTo(((fz.xMax + ez.xMin) / 2) * s, h);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  const tint = STORM_TINT[stage] ?? 0;
  if (tint > 0) {
    ctx.fillStyle = `rgba(26, 28, 50, ${tint})`;
    ctx.fillRect(0, 0, w, h);
  }
  // K18: Starkregen dunkelt das Ufer ab
  if (rain > 1) {
    ctx.fillStyle = `rgba(20, 30, 45, ${Math.min(0.45, (rain - 1) * 0.3)})`;
    ctx.fillRect(0, 0, w, h);
  }
}

/** R25 (Fabi, Runde 7): Regen über dem Ufer, an festen Stellen, die mit der Zeit fallen; im Starkregen mehr */
export function drawRain(ctx: CanvasRenderingContext2D, s: number, stage: string, t: number, rain: number): void {
  const n = Math.round((STORM_RAIN[stage] ?? 0) * Math.min(2, rain));
  if (!n) return;
  ctx.strokeStyle = 'rgba(205, 220, 238, 0.35)';
  ctx.lineWidth = Math.max(1, s / 9);
  ctx.beginPath();
  const H = FIELD.height + 12;
  for (let i = 0; i < n; i++) {
    // goldener Schnitt: gleichmäßig verstreut, ohne Paare
    const speed = 55 + ((i * 0.7548) % 1) * 30;
    const x0 = ((i * 0.618034) % 1) * (FIELD.width + 30);
    const y = ((t * speed + ((i * 0.381966) % 1) * H) % H) - 6;
    const x = x0 - y * 0.25;
    ctx.moveTo(x * s, y * s);
    ctx.lineTo((x - 0.9) * s, (y + 3.6) * s);
  }
  ctx.stroke();
}

function bar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, share: number, color: string, ghost?: number): void {
  const clamp = (v: number) => Math.max(0, Math.min(1, v));
  ctx.fillStyle = C.hpBack;
  ctx.fillRect(x - w / 2, y, w, h);
  // T12 (Fabi, Spieltest Phase 4): Der eben verlorene Teil bleibt kurz hell stehen, dann schrumpft er nach
  if (ghost !== undefined && ghost > share + 1e-3) {
    ctx.fillStyle = 'rgba(255, 250, 235, 0.9)';
    ctx.fillRect(x - w / 2 + w * clamp(share), y, w * (clamp(ghost) - clamp(share)), h);
  }
  ctx.fillStyle = color;
  ctx.fillRect(x - w / 2, y, w * clamp(share), h);
}

/**
 * T18 (Fabi, Runde 8): Ein Schild verlängert den Lebensbalken nach rechts als hellblaues Stück. Reicht Leben plus Schild über
 * das Höchstleben hinaus, wird der Balken gestaucht, damit das Schild auch bei vollem Leben zu sehen ist.
 */
function shieldBar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, hp: number, maxHp: number, shield: number, color: string, ghostHp?: number): void {
  const total = Math.max(maxHp, hp + shield);
  const left = x - w / 2;
  const hpW = (w * Math.max(0, hp)) / total;
  ctx.fillStyle = C.hpBack;
  ctx.fillRect(left, y, w, h);
  if (ghostHp !== undefined && ghostHp > hp) {
    ctx.fillStyle = 'rgba(255, 250, 235, 0.9)';
    ctx.fillRect(left + hpW, y, (w * Math.min(ghostHp - hp, total - hp)) / total, h);
  }
  ctx.fillStyle = color;
  ctx.fillRect(left, y, hpW, h);
  ctx.fillStyle = '#bfe3fa';
  ctx.fillRect(left + hpW, y, (w * shield) / total, h);
  ctx.strokeStyle = 'rgba(159, 208, 240, 0.95)';
  ctx.lineWidth = Math.max(1, h * 0.25);
  ctx.strokeRect(left - 0.5, y - 0.5, w + 1, h + 1);
}

const STATUS_GLYPHS: [number, string, string][] = [
  [FrameFlag.rolled, 'eingerollt', '#e6c35c'],
  [FrameFlag.guarded, 'Bau', '#b08a64'],
  [FrameFlag.taunted, '→Igel', '#d9895b'],
  [FrameFlag.fleeing, 'flieht!', '#ffb3a3'],
  [FrameFlag.stunned, 'benommen', '#fff7c2'],
  [FrameFlag.enraged, 'wütend', '#ff8a6b'],
  [FrameFlag.hasted, 'schnell', '#7fb2d9'],
  [FrameFlag.slowed, 'langsam', '#a7a29a'],
  [FrameFlag.poisoned, 'Gift', '#8fc27a'],
  [FrameFlag.spinning, 'kreiselt', '#e6c35c'],
  [FrameFlag.shielded, 'Schild', '#9fd0f0'],
  [FrameFlag.burrowed, 'eingegraben', '#b08a64'],
  [FrameFlag.empowered, 'Nuss!', '#f6c76b'],
  [FrameFlag.climbing, 'klettert', '#b08a64'],
  [FrameFlag.blinded, 'Dreck in den Augen', '#a98458'],
  [FrameFlag.timid, 'zaghaft', '#b9b7e0'],
  [FrameFlag.guarding, 'Leibwache', '#f6c76b'],
];

/** Fabi (Phase Kern): benommen, mit grauem Schleier und drei kreisenden Sternen über dem Kopf */
function drawDizzy(ctx: CanvasRenderingContext2D, s: number, x: number, y: number, r: number, t: number): void {
  ctx.save();
  ctx.fillStyle = 'rgba(150, 150, 160, 0.55)';
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  const cy = y - r - s * 1.2;
  const rx = Math.max(r * 0.9, s * 2.4);
  const ry = rx * 0.35;
  for (let i = 0; i < 3; i++) {
    const a = t * 5 + (i * Math.PI * 2) / 3;
    const px = x + Math.cos(a) * rx;
    const py = cy + Math.sin(a) * ry;
    const k = Math.max(3, s * 0.95) * (0.8 + 0.2 * Math.sin(a));
    ctx.fillStyle = '#fff3a8';
    ctx.strokeStyle = 'rgba(60, 50, 10, 0.8)';
    ctx.lineWidth = Math.max(1, s * 0.2);
    ctx.beginPath();
    for (let j = 0; j < 8; j++) {
      const ang = (j * Math.PI) / 4 - Math.PI / 2;
      const rad = j % 2 === 0 ? k : k * 0.42;
      const qx = px + Math.cos(ang) * rad;
      const qy = py + Math.sin(ang) * rad;
      if (j === 0) ctx.moveTo(qx, qy);
      else ctx.lineTo(qx, qy);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/** F42: ein rotes Kreuz mit hellem Rand, wie ein Pflaster auf der Figur */
function drawWound(ctx: CanvasRenderingContext2D, s: number, x: number, y: number): void {
  const k = Math.max(3, s * 1.15);
  const w = k * 0.42;
  ctx.save();
  ctx.fillStyle = '#fff7ea';
  ctx.fillRect(x - w / 2 - 1, y - k - 1, w + 2, k * 2 + 2);
  ctx.fillRect(x - k - 1, y - w / 2 - 1, k * 2 + 2, w + 2);
  ctx.fillStyle = '#d6453b';
  ctx.fillRect(x - w / 2, y - k, w, k * 2);
  ctx.fillRect(x - k, y - w / 2, k * 2, w);
  ctx.restore();
}

/** K35: kleine rote Kerben, mittig über dem Lebensbalken; ab sechs steht die Zahl daneben */
function drawNotches(ctx: CanvasRenderingContext2D, s: number, x: number, bottom: number, n: number): void {
  const shown = Math.min(n, 6);
  const w = Math.max(4, s * 0.9);
  const h = Math.max(9, s * 2.4);
  const gap = Math.max(2, s * 0.55);
  const total = shown * w + (shown - 1) * gap;
  ctx.save();
  ctx.fillStyle = '#ff4a3d';
  ctx.strokeStyle = 'rgba(255, 240, 230, 0.9)';
  ctx.lineWidth = Math.max(1, s * 0.2);
  for (let i = 0; i < shown; i++) {
    const left = x - total / 2 + i * (w + gap);
    ctx.beginPath();
    // leicht schräg, wie eingeritzt
    ctx.moveTo(left + w * 0.35, bottom - h);
    ctx.lineTo(left + w, bottom - h);
    ctx.lineTo(left + w * 0.65, bottom);
    ctx.lineTo(left, bottom);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  if (n > shown) {
    ctx.font = `700 ${Math.max(9, s * 1.6)}px ${FONT.body}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`+${n - shown}`, x + total / 2 + gap, bottom);
  }
  ctx.restore();
}

/** S10 Leibwache (Phase Kern): gestrichelte Linie vom Igel zu seinem Schützling, über dem Schützling ein kleiner Schild */
export function drawWardLink(ctx: CanvasRenderingContext2D, s: number, from: { x: number; y: number; radius: number }, to: { x: number; y: number; radius: number }, strong: boolean, icon = true): void {
  ctx.save();
  ctx.strokeStyle = '#f6c76b';
  ctx.globalAlpha = strong ? 0.9 : 0.5;
  ctx.lineWidth = Math.max(1.5, s * (strong ? 0.45 : 0.3));
  ctx.setLineDash([s * 0.8, s * 0.9]);
  ctx.beginPath();
  ctx.moveTo(from.x * s, from.y * s);
  ctx.lineTo(to.x * s, to.y * s);
  ctx.stroke();
  ctx.setLineDash([]);
  if (icon) drawShieldIcon(ctx, s, to.x + to.radius * 0.8, to.y + to.radius * 0.75, '#f6c76b');
  ctx.restore();
}

/** S10: kleines Schild-Zeichen, für den Griff am Igel und über dem Schützling */
export function drawShieldIcon(ctx: CanvasRenderingContext2D, s: number, x: number, y: number, color: string): void {
  const k = Math.max(4, s * 1.25);
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(40, 30, 10, 0.85)';
  ctx.lineWidth = Math.max(1, s * 0.2);
  ctx.beginPath();
  ctx.moveTo(x * s - k, y * s - k * 0.9);
  ctx.lineTo(x * s + k, y * s - k * 0.9);
  ctx.lineTo(x * s + k, y * s - k * 0.1);
  ctx.quadraticCurveTo(x * s + k * 0.8, y * s + k * 0.8, x * s, y * s + k * 1.15);
  ctx.quadraticCurveTo(x * s - k * 0.8, y * s + k * 0.8, x * s - k, y * s - k * 0.1);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/** F32: Giftstapel als grüner Tropfen mit Zahl */
function drawDrop(ctx: CanvasRenderingContext2D, s: number, px: number, py: number, n: number): void {
  const pr = Math.max(s * 1.5, 6);
  ctx.save();
  ctx.fillStyle = '#6fbf4a';
  ctx.strokeStyle = 'rgba(20, 40, 15, 0.8)';
  ctx.lineWidth = Math.max(1, s * 0.25);
  ctx.beginPath();
  ctx.moveTo(px, py - pr * 1.5);
  ctx.quadraticCurveTo(px + pr, py - pr * 0.3, px + pr * 0.85, py + pr * 0.25);
  ctx.arc(px, py + pr * 0.25, pr * 0.85, 0, Math.PI);
  ctx.quadraticCurveTo(px - pr, py - pr * 0.3, px, py - pr * 1.5);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#10200a';
  ctx.font = `700 ${Math.max(8, pr * 1.05)}px ${FONT.body}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(n), px, py + pr * 0.2);
  ctx.restore();
}

/** Übungsufer (K49): Hindernisse, die zwischen den Figuren liegen; y ist ihre Tiefe, wer weiter hinten steht, kommt vorher */
export type Prop = { y: number; draw: () => void };

export function drawUnits(ctx: CanvasRenderingContext2D, s: number, units: DrawUnit[], opts: { highlight: number | null; showTraits: boolean; ghosts?: boolean; t?: number; props?: Prop[] }): void {
  const t = opts.t ?? 0;
  traitSpots.length = 0;
  // Phase Kern: Figuren statt Kreise, wo Bilder da sind (T21); wer gerade Abschied nimmt, bleibt noch kurz zu sehen
  const figures = new Map<number, FigureGeo>();
  for (const u of units) {
    const g = isAlive(u) || dying(u) ? figureGeo(u, s) : null;
    if (g) figures.set(u.uid, g);
  }
  // Bäume zuerst, damit das Eichhörnchen obendrauf sitzt; Figuren nach dem Fußpunkt, die hinteren zuerst
  const treeFirst = (a: DrawUnit, b: DrawUnit) => (a.kind === 'tree' ? 0 : 1) - (b.kind === 'tree' ? 0 : 1);
  const ordered = [...units].sort((a, b) => (figures.size ? a.y - b.y || treeFirst(a, b) : treeFirst(a, b)));
  const drops: { x: number; y: number; n: number }[] = [];
  const overlays: Overlay[] = [];
  // Version 3: was am Boden liegt (Erdmulde, Erdloch, Boing), unter allen Figuren
  for (const u of ordered) {
    const g = figures.get(u.uid);
    if (g) drawFx(ctx, s, u, g, 'boden');
  }
  const props = [...(opts.props ?? [])].sort((a, b) => a.y - b.y);
  let next = 0;
  for (const u of ordered) {
    while (next < props.length && props[next]!.y <= u.y) props[next++]!.draw();
    const g = figures.get(u.uid);
    if (!isAlive(u)) {
      if (g && dying(u)) {
        drawFigure(ctx, s, u, g, t);
        continue;
      }
      // Beim Betrachten: blasser, gestrichelter Umriss dort, wo die Figur das Ufer verlassen hat
      // K21 (Fabi, Runde 6): Wer über den Rand geschubst wurde, wartet dort sichtbar bis zum Ende des Kampfs
      if ((opts.ghosts || (u.flags & FrameFlag.pushedOut) !== 0) && u.goneLabel) drawGhost(ctx, s, u, opts.highlight === u.uid);
      continue;
    }
    if (g) overlays.push(drawFigureUnit(ctx, s, u, g, units, opts, t, drops));
    else drawCircleUnit(ctx, s, u, opts, t, drops);
  }
  while (next < props.length) props[next++]!.draw();
  // Version 4 (bedroht-Bögen): nach allen Figuren und vor den Leisten, sonst verdeckt sie der Angreifer selbst
  for (const u of ordered) {
    const g = figures.get(u.uid);
    if (g && isAlive(u) && u.attackerFrom?.length) drawThreatArcs(ctx, u, g, u.y * s + ((u.pose?.drift ?? 0) + (u.pose?.lunge?.y ?? 0)) * s, 'front');
  }
  // K40 (Fabi, Phase Kern): Leisten, Zeichen und Namen liegen über allen Figuren, damit nichts verdeckt wird.
  // K45 (Fabi, 17.10.2026 nachts): Liegt eine Leiste über einer anderen Figur, scheint sie durch, damit man sieht, was
  // dahinter passiert; wer gerade gezeigt oder angeklickt wird, hat sie voll
  for (const o of overlays) {
    const covers = units.some((v) => {
      if (v.uid === o.uid || v.kind === 'tree' || !isAlive(v)) return false;
      const g = figures.get(v.uid);
      if (!g) return false;
      const half = g.w / 2;
      return g.x + half > o.box.x0 && g.x - half < o.box.x1 && g.y > o.box.y0 && g.top < o.box.y1;
    });
    o.draw(covers && opts.highlight !== o.uid ? OVERLAY_SEE_THROUGH : 1);
  }
  for (const d of drops) drawDrop(ctx, s, d.x, d.y, d.n);
}

const isAlive = (u: DrawUnit) => (u.flags & FrameFlag.alive) !== 0;
/** T31: Abschied, Fallen oder Vertreiben läuft noch; wer gefallen ist, liegt blass bis zum Ende des Kampfs */
const dying = (u: DrawUnit) => !!u.pose && u.pose.down && u.pose.fade < 1;

type UnitOpts = { highlight: number | null; showTraits: boolean };
type Overlay = { uid: number; box: { x0: number; x1: number; y0: number; y1: number }; draw: (alpha: number) => void };
/** K45: so deckend ist eine Leiste, hinter der eine andere Figur steht */
const OVERLAY_SEE_THROUGH = 0.35;

/** Phase Kern: die Figur mit allem, was bisher am Kreis hing; Leisten, Zeichen und Namen kommen als Nachtrag zurück (K40) */
function drawFigureUnit(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, g: FigureGeo, all: DrawUnit[], opts: UnitOpts, t: number, drops: { x: number; y: number; n: number }[]): Overlay {
  const anims = g.sheet?.animations ?? {};
  // Version 3: Wo der Grafik-Lead eine Bewegung gezeichnet hat (eingraben, rollen), braucht es die Zeichen aus Code nicht
  const burrowed = (u.flags & FrameFlag.burrowed) !== 0 && !anims.eingraben && !anims.abtauchen;
  const ground = u.y * s;
  const fxIds = new Set((u.fx ?? []).map((f) => f.id));
  ctx.globalAlpha = (u.flags & FrameFlag.fleeing) !== 0 && !fxIds.has('anzeige-feige-flucht') ? 0.6 : 1;
  const body = Math.max(g.w, g.y - g.top) / 2;
  if (burrowed) {
    // R27 Erdüberraschung: ein Erdhügel, unter dem er gräbt
    ctx.save();
    ctx.fillStyle = '#6b4f36';
    ctx.beginPath();
    ctx.ellipse(g.x, ground, g.ringRx * 1.2, g.ringRx * 0.6, 0, Math.PI, 0);
    ctx.fill();
    ctx.restore();
  }
  if ((u.flags & FrameFlag.empowered) !== 0 && !u.glow) {
    // R27 Nussgeschenk und Raserei: warmes Leuchten
    ctx.save();
    ctx.globalAlpha = 0.3 + 0.2 * Math.sin(t * 8);
    ctx.fillStyle = '#f6c76b';
    ctx.beginPath();
    ctx.arc(g.x, g.cy, body + s * 1.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  if ((u.flags & FrameFlag.guarded) !== 0 && !fxIds.has('erdwall-vorn') && !fxIds.has('erdschutz')) {
    // Bau graben: warmer Ring
    ctx.save();
    ctx.strokeStyle = 'rgba(176, 138, 100, 0.85)';
    ctx.lineWidth = s * 0.8;
    ctx.beginPath();
    ctx.ellipse(g.x, ground, g.ringRx + s * 1.2, g.ringRy + s * 0.6, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
  drawFigureGround(ctx, s, u, g, opts.highlight === u.uid);
  // Version 4: das kleine Klassenzeichen vorn auf dem Ring, mit dem Ring gezeichnet
  if (u.kind !== 'tree' && (u.flags & FrameFlag.onTree) === 0) drawSmallClass(ctx, u, g, ground + (u.pose?.drift ?? 0) * s);
  // Version 6: der hintere Teil der Bögen kommt mit dem Ring, unter die eigene Figur
  if (u.attackerFrom?.length && u.kind !== 'tree') drawThreatArcs(ctx, u, g, u.y * s + ((u.pose?.drift ?? 0) + (u.pose?.lunge?.y ?? 0)) * s, 'back');
  // K45: rote Spitzen am Ring, wenn der Grafik-Lead (noch) keine Bögen gezeichnet hat; die Bögen kommen nach allen Figuren
  if (u.attackerFrom?.length && !sprite('marker', 'bedroht-12')) drawThreatTicks(ctx, s, u, g);
  if (u.kind === 'tree') {
    const occupied = all.some((o) => o.kind !== 'tree' && isAlive(o) && (o.flags & FrameFlag.onTree) !== 0 && Math.abs(o.x - u.x) < 0.6 && Math.abs(o.y - u.y) < 0.6);
    drawFigure(ctx, s, u, g, t, occupied);
  } else {
    drawMotionLines(ctx, u, g, t);
    drawFx(ctx, s, u, g, 'hinter');
    ctx.save();
    if (burrowed) ctx.globalAlpha *= 0.3;
    drawFigure(ctx, s, u, g, t);
    ctx.restore();
    // Fabi (Phase Kern): Benommen sieht man sofort, die Figur wird grau
    if ((u.flags & FrameFlag.stunned) !== 0) drawFigureVeil(ctx, u, g, t);
    if ((u.flags & FrameFlag.spinning) !== 0 && !anims.rollen) drawSpikes(ctx, s, g.x, g.cy, body * 0.75, t, true);
    if ((u.flags & FrameFlag.rolled) !== 0 && !anims.rollen) drawSpikes(ctx, s, g.x, g.cy, body * 0.7, t, false);
    drawFx(ctx, s, u, g, 'vor');
  }
  if ((u.flags & FrameFlag.shielded) !== 0 && ![...fxIds].some((id) => id.startsWith('stoeckchenwall-'))) {
    // F35 Schutzschild: eine helle Blase (der Stöckchenwall des Bibers hat seinen eigenen Kranz)
    ctx.save();
    ctx.strokeStyle = 'rgba(159, 208, 240, 0.9)';
    ctx.fillStyle = 'rgba(159, 208, 240, 0.15)';
    ctx.lineWidth = s * 0.45;
    ctx.beginPath();
    ctx.arc(g.x, g.cy, body + s * 1.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
  const woundDrawn = drawFigureStatus(ctx, s, u, g, t);
  if (u.wounded && !woundDrawn) drawWound(ctx, s, g.x - g.w * 0.3, g.cy);
  if ((u.flags & FrameFlag.stunned) !== 0 && !sprite('status', 'benommen') && !hasSmallSigns()) drawDizzy(ctx, s, g.x, g.cy, body * 0.8, t);
  const alpha = ctx.globalAlpha;
  ctx.globalAlpha = 1;
  const l = overlayLayout(u, g, s, opts);
  return {
    uid: u.uid,
    box: l.box,
    draw: (fade) => {
      ctx.globalAlpha = alpha * fade;
      drawFigureOverlay(ctx, s, u, g, opts, ground, drops, l, t);
      ctx.globalAlpha = alpha;
      // Name und Zustandswörter beim Darüberfahren immer voll
      if (!u.pose || opts.highlight === u.uid) drawWords(ctx, s, u, opts, g.x, ground + g.ringRy + s * 0.5);
      ctx.globalAlpha = 1;
    },
  };
}

type OverlayLayout = { bw: number; hpH: number; mutH: number; barY: number; classIcon: boolean; box: Overlay['box']; /** Version 4: die ruhige Leiste */ calm?: CalmLayout };

/** K40, K45: wo Leisten und Zeichen über der Figur liegen; daraus auch, ob sie eine andere Figur verdecken */
function overlayLayout(u: DrawUnit, g: FigureGeo, s: number, opts: UnitOpts): OverlayLayout {
  // Version 4: ruhige Leiste des Grafik-Leads; ohne Klassenzeichen daneben (das steht klein vorn am Ring)
  const spec = u.kind !== 'tree' ? calmBar() : null;
  if (spec) {
    const c = calmLayout(u, g, s, spec);
    const icon = 11 * g.k;
    const traits = u.traitIds?.length ?? 0;
    const left = u.kind === 'stormling' ? traits : 0;
    const right = (u.kind === 'infested' ? traits : 0) * icon + 3 * 9 * g.k + ((u.poison ?? 0) > 0 ? 8 * g.k : 0);
    return { bw: c.w, hpH: c.h, mutH: 0, barY: c.y0, classIcon: false, calm: c, box: { x0: c.x0 - left * icon, x1: c.x0 + c.w + right, y0: c.y0 - g.k, y1: c.y0 + c.h + g.k } };
  }
  const bw = Math.max(g.w * 0.8, s * 6.5);
  const hpH = s * 0.8;
  const mutH = s * 0.4;
  const barY = Math.max(s * 0.3, g.top - s * 2.2);
  // K45: Das Zeichen der Klasse steht beim Aufstellen und beim Darüberfahren; im Kampf zeigt der farbige Ring die Klasse
  const classIcon = !u.pose || opts.highlight === u.uid;
  const icon = 11 * g.k;
  const traits = u.traitIds?.length ?? 0;
  const left = (u.kind === 'stormling' ? traits : 0) + (classIcon && (u.kind === 'friend' || u.kind === 'infested') ? 1 : 0);
  const right = (u.kind === 'infested' ? traits : 0) + ((u.poison ?? 0) > 0 ? 1.4 : 0);
  return { bw, hpH, mutH, barY, classIcon, box: { x0: g.x - bw / 2 - left * icon, x1: g.x + bw / 2 + right * icon, y0: barY - s * 0.6, y1: barY + hpH + mutH + s * 0.4 } };
}

/** Version 4: Was die Figur mit ihrer eigenen Bewegung zeigt, braucht kein kleines Zeichen in der Reihe */
function shownByFigure(u: DrawUnit, g: FigureGeo): Set<string> {
  const anims = g.sheet?.animations ?? {};
  const out = new Set<string>();
  if (anims.rollen) {
    out.add('rolled');
    out.add('spinning');
  }
  if (anims.eingraben) out.add('burrowed');
  if (sprite('effect', 'erdwall-vorn')) out.add('guarded');
  out.add('climbing');
  if (sprite('status', 'wunde')) out.add('wounded');
  return out;
}

/**
 * K45 (Fabi, 17.10.2026 nachts): Statt roter Striche über dem Kopf kleine rote Spitzen außen am Bodenring, eine je
 * Sturmwesen, das ihn angreift (beim Aufstellen: zuerst angreifen würde), dort, von wo es kommt. Kommen mehrere aus
 * derselben Richtung, rücken die Spitzen ein wenig auseinander
 */
function drawThreatTicks(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, g: FigureGeo): void {
  const from = u.attackerFrom ?? [];
  const angles = from.map((a) => Math.atan2(a.y - u.y, a.x - u.x)).sort((a, b) => a - b);
  const spread = 0.32;
  for (let i = 1; i < angles.length; i++) if (angles[i]! - angles[i - 1]! < spread) angles[i] = angles[i - 1]! + spread;
  const gy = u.y * s + ((u.pose?.drift ?? 0) + (u.pose?.lunge?.y ?? 0)) * s;
  const rx = g.ringRx + g.k * 1.2;
  const ry = g.ringRy + g.k * 0.6;
  const len = Math.max(6, g.k * 5);
  const half = Math.max(3, g.k * 2.2);
  ctx.save();
  ctx.globalAlpha *= 1 - (u.pose?.fade ?? 0);
  ctx.fillStyle = '#ff4a3d';
  ctx.strokeStyle = 'rgba(20, 10, 10, 0.75)';
  ctx.lineWidth = Math.max(1, g.k * 0.5);
  ctx.lineJoin = 'round';
  for (const a of angles) {
    const c = Math.cos(a);
    const sn = Math.sin(a);
    // Spitze am Ring, Grundseite außen: zeigt auf den Freund
    const tx = g.x + rx * c;
    const ty = gy + ry * sn;
    const nx = c;
    const ny = sn * 0.6;
    const l = Math.hypot(nx, ny) || 1;
    const ox = (nx / l) * len;
    const oy = (ny / l) * len;
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(tx + ox - (oy / len) * half, ty + oy + (ox / len) * half);
    ctx.lineTo(tx + ox + (oy / len) * half, ty + oy - (ox / len) * half);
    ctx.closePath();
    ctx.stroke();
    ctx.fill();
  }
  ctx.restore();
}

/**
 * K40 (Fabi, Phase Kern): Leben und Mut als schmale Leisten über allen Figuren, daneben Klasse und Eigenschaften; Name
 * und Zustandswörter nur beim Aufstellen und beim Darüberfahren, im Kampf sagen Figur und Zeichen, wer es ist
 */
function drawFigureOverlay(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, g: FigureGeo, opts: UnitOpts, ground: number, drops: { x: number; y: number; n: number }[], l: OverlayLayout, t: number): void {
  const spec = calmBar();
  if (l.calm && spec) {
    // Version 4: ruhige Leiste, Eigenschaften der Sturmwesen links daneben, rechts die kleinen Zustandszeichen
    drawCalmBar(ctx, u, l.calm, spec);
    const right = drawSigns(ctx, u, g, g.x, l.calm.y0, l.calm.w, l.calm.h, false);
    drawSmallStatus(ctx, u, g, Math.max(right - 2 * g.k, l.calm.x0 + l.calm.w), l.calm.y0 + l.calm.h / 2, t, shownByFigure(u, g));
    if ((u.flags & FrameFlag.confused) !== 0 && !sprite('status', 'anzeige-verpeilt') && !sprite('status', 'verpeilt-klein')) drawConfused(ctx, s, g.x, l.calm.y0 - s * 2.6);
    return;
  }
  // Leben und Mut (K6, K19) über dem Kopf
  const { bw, hpH, mutH, barY } = l;
  const share = u.hp / u.maxHp;
  const ghost = u.ghostHp !== undefined ? u.ghostHp / u.maxHp : undefined;
  if (u.kind === 'tree') bar(ctx, g.x, ground + s * 0.8, bw, s * 0.9, share, share < 0.34 ? C.hpLow : '#7fae6f', ghost);
  else if ((u.shield ?? 0) > 0.5) shieldBar(ctx, g.x, barY, bw, hpH, u.hp, u.maxHp, u.shield!, share < 0.34 ? C.hpLow : C.hp, u.ghostHp);
  else bar(ctx, g.x, barY, bw, hpH, share, share < 0.34 ? C.hpLow : C.hp, ghost);
  if (u.kind !== 'tree') {
    bar(ctx, g.x, barY + hpH, bw, mutH, u.mut / (u.mutMax ?? combatRules.mut.max), C.mut);
    // T21, K38: Zeichen der Klasse und der Eigenschaften neben dem Balken
    drawSigns(ctx, u, g, g.x, barY, bw, hpH + mutH, l.classIcon);
  }
  if ((u.poison ?? 0) > 0) {
    // F32: Giftstapel, beim Bild neben der Lebensleiste
    const right = g.x + bw / 2 + g.k * 4 + (u.kind === 'infested' ? (u.traitIds?.length ?? 0) * 11 * g.k : 0);
    if (!drawPoisonSign(ctx, s, right, barY + s * 0.8, g.k, u.poison!)) drops.push({ x: g.x + g.w * 0.45, y: g.cy, n: u.poison! });
  }
  if ((u.flags & FrameFlag.marked) !== 0) {
    // Schwachstelle des Eisvogels (S5)
    const m = s * 1.6;
    const mx = g.x + g.w * 0.35;
    const my = g.top + s * 1.2;
    ctx.strokeStyle = C.mark;
    ctx.lineWidth = s * 0.55;
    ctx.beginPath();
    ctx.moveTo(mx - m, my - m);
    ctx.lineTo(mx + m, my + m);
    ctx.moveTo(mx + m, my - m);
    ctx.lineTo(mx - m, my + m);
    ctx.stroke();
  }
  if ((u.flags & FrameFlag.confused) !== 0 && !sprite('status', 'anzeige-verpeilt')) drawConfused(ctx, s, g.x, barY - s * 2.6);
}

/** Der Kreis wie bisher (Phase 3 bis Kern, Runde 1): mit der Taste G zum Vergleich */
function drawCircleUnit(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, opts: UnitOpts, t: number, drops: { x: number; y: number; n: number }[]): void {
  const onTree = (u.flags & FrameFlag.onTree) !== 0;
  const burrowed = (u.flags & FrameFlag.burrowed) !== 0;
  const hit = u.hit ?? 0;
  // T12 Wucht: Wer hart getroffen wird, wackelt kurz und wird ein wenig größer
  const r = (onTree ? u.radius * 0.75 : u.radius) * s * (1 + hit * 0.18);
  const x = u.x * s + (hit > 0 ? Math.sin(t * 90 + u.uid) * hit * s * 1.1 : 0);
  const y = (onTree ? u.y - 1.5 : u.y) * s;
  ctx.globalAlpha = (u.flags & FrameFlag.fleeing) !== 0 ? 0.6 : burrowed ? 0.35 : 1;

  // R27 Erdüberraschung: ein Erdhügel, unter dem er gräbt
  if (burrowed) {
    ctx.save();
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = '#6b4f36';
    ctx.beginPath();
    ctx.ellipse(x, y + r * 0.35, r * 1.2, r * 0.6, 0, Math.PI, 0);
    ctx.fill();
    ctx.restore();
  }
  // R27 Nussgeschenk und Raserei: warmes Leuchten
  if ((u.flags & FrameFlag.empowered) !== 0) {
    ctx.save();
    ctx.globalAlpha = 0.35 + 0.25 * Math.sin(t * 8);
    ctx.fillStyle = '#f6c76b';
    ctx.beginPath();
    ctx.arc(x, y, r + s * 1.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Bau graben: warmer Ring
  if ((u.flags & FrameFlag.guarded) !== 0) {
    ctx.strokeStyle = 'rgba(176, 138, 100, 0.8)';
    ctx.lineWidth = s * 0.9;
    ctx.beginPath();
    ctx.arc(x, y, r + s * 1.1, 0, Math.PI * 2);
    ctx.stroke();
  }

  if (u.kind === 'tree') {
    ctx.fillStyle = TREE_COLOR;
    ctx.beginPath();
    for (let i = 0; i < 3; i++) ctx.arc(x + Math.cos(i * 2.1) * r * 0.35, y + Math.sin(i * 2.1) * r * 0.35, r * 0.75, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.fillStyle = u.color;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = Math.max(1, s * 0.35);
    ctx.strokeStyle = u.side === 'enemy' ? '#2a2940' : 'rgba(40, 30, 20, 0.6)';
    ctx.stroke();
    // F35 Stachelball: kreiselnde Stacheln
    if ((u.flags & FrameFlag.spinning) !== 0) drawSpikes(ctx, s, x, y, r, t, true);
    if ((u.flags & FrameFlag.rolled) !== 0) drawSpikes(ctx, s, x, y, r, t, false);
    ctx.fillStyle = u.side === 'enemy' ? C.cream : '#2a2118';
    ctx.font = `600 ${Math.max(9, r * 0.85)}px ${FONT.body}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(u.short, x, y + r * 0.05);
  }

  // F35 Schutzschild: eine helle Blase
  if ((u.flags & FrameFlag.shielded) !== 0) {
    ctx.save();
    ctx.strokeStyle = 'rgba(159, 208, 240, 0.9)';
    ctx.fillStyle = 'rgba(159, 208, 240, 0.15)';
    ctx.lineWidth = s * 0.5;
    ctx.beginPath();
    ctx.arc(x, y, r + s * 1.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
  // T12 Wucht: kurzer heller Blitz beim Treffer
  if (hit > 0 && u.kind !== 'tree') {
    ctx.save();
    ctx.globalAlpha = Math.min(0.85, 0.25 + hit * 0.8);
    ctx.fillStyle = '#fffaf0';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Fabi (Phase Kern): Benommen sieht man sofort, die Figur wird grau, und über dem Kopf kreisen Sterne
  if ((u.flags & FrameFlag.stunned) !== 0 && u.kind !== 'tree') drawDizzy(ctx, s, x, y, r, t);
  // F42 (Fabi, Phase Kern): Die Wunde sieht man an der Figur, als rotes Kreuz oben links
  if (u.wounded) drawWound(ctx, s, x - r * 0.72, y - r * 0.72);

  if (opts.highlight === u.uid) {
    ctx.strokeStyle = C.cream;
    ctx.lineWidth = Math.max(1.5, s * 0.4);
    ctx.beginPath();
    ctx.arc(x, y, r + s * 1.8, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Leben und Mut (K6, K19)
  const bw = Math.max(r * 2.2, s * 7);
  const share = u.hp / u.maxHp;
  const ghost = u.ghostHp !== undefined ? u.ghostHp / u.maxHp : undefined;
  // K29 (Fabi, nach Runde 8): Man soll immer sehen, wie viel Leben jemand hat. Wird eine Figur ganz an den oberen Rand
  // geschoben, rutschen Lebens- und Mutbalken nicht aus dem Bild, sondern bleiben am Rand stehen
  const barY = Math.max(s * 0.3, y - r - s * 2.4);
  // Der Baum trägt seine Leiste unten, damit sie nicht mit der des Eichhörnchens kollidiert
  if (u.kind === 'tree') bar(ctx, x, y + r + s * 0.8, bw, s * 0.9, share, share < 0.34 ? C.hpLow : '#7fae6f', ghost);
  else if ((u.shield ?? 0) > 0.5) shieldBar(ctx, x, barY, bw, s * 1.1, u.hp, u.maxHp, u.shield!, share < 0.34 ? C.hpLow : C.hp, u.ghostHp);
  else bar(ctx, x, barY, bw, s * 1.1, share, share < 0.34 ? C.hpLow : C.hp, ghost);
  if (u.kind !== 'tree') bar(ctx, x, barY + s * 1.1, bw, s * 0.55, u.mut / (u.mutMax ?? combatRules.mut.max), C.mut);
  // K35 (Fabi, Phase Kern): rote Kerben über dem Lebensbalken, eine je Sturmwesen, das ihn angreift
  if ((u.attackers ?? 0) > 0) drawNotches(ctx, s, x, barY - s * 0.5, u.attackers!);

  // F32 (Fabi, Spieltest Phase 4): Giftstapel als grüner Tropfen mit Zahl; Hilfsbericht: erst nach allen Figuren,
  // damit keine andere Figur ihn verdeckt
  if ((u.poison ?? 0) > 0) drops.push({ x: x + r * 0.95, y: y + r * 0.55, n: u.poison! });

  // Schwachstelle des Eisvogels (S5)
  if ((u.flags & FrameFlag.marked) !== 0) {
    ctx.strokeStyle = C.mark;
    ctx.lineWidth = s * 0.6;
    const m = r * 0.55;
    ctx.beginPath();
    ctx.moveTo(x + r * 0.9 - m, y - r * 0.9 - m);
    ctx.lineTo(x + r * 0.9 + m, y - r * 0.9 + m);
    ctx.moveTo(x + r * 0.9 + m, y - r * 0.9 - m);
    ctx.lineTo(x + r * 0.9 - m, y - r * 0.9 + m);
    ctx.stroke();
  }

  // K23 Verpeilt (Fabi, Runde 7): Ein großes Fragezeichen blitzt über dem Kopf auf, wenn er seinen Kniff vergisst
  if ((u.flags & FrameFlag.confused) !== 0) drawConfused(ctx, s, x, y - r - s);
  drawWords(ctx, s, u, opts, x, y + r + s * 0.6);
  ctx.globalAlpha = 1;
}

/** F35 Stachelball (kreiselnd) und Einrollen (still): Stacheln rund um die Figur */
function drawSpikes(ctx: CanvasRenderingContext2D, s: number, x: number, y: number, r: number, t: number, spinning: boolean): void {
  ctx.save();
  if (spinning) {
    ctx.strokeStyle = '#3a2d20';
    ctx.lineWidth = s * 0.55;
    const spin = t * 14;
    for (let i = 0; i < 10; i++) {
      const a = spin + (i / 10) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * r * 0.7, y + Math.sin(a) * r * 0.7);
      ctx.lineTo(x + Math.cos(a + 0.35) * r * 1.35, y + Math.sin(a + 0.35) * r * 1.35);
      ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(230, 195, 92, 0.7)';
    ctx.lineWidth = s * 0.35;
    ctx.beginPath();
    ctx.arc(x, y, r * 1.55, spin, spin + Math.PI * 1.2);
    ctx.stroke();
  } else {
    ctx.strokeStyle = '#3a2d20';
    ctx.lineWidth = s * 0.5;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * r * 0.6, y + Math.sin(a) * r * 0.6);
      ctx.lineTo(x + Math.cos(a) * r * 1.15, y + Math.sin(a) * r * 1.15);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** K23 Verpeilt (Fabi, Runde 7): ein großes Fragezeichen über dem Kopf */
function drawConfused(ctx: CanvasRenderingContext2D, s: number, x: number, bottom: number): void {
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.lineWidth = Math.max(1, s * 0.4);
  ctx.font = `800 ${Math.max(14, s * 4.5)}px ${FONT.title}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.strokeText('?', x, bottom);
  ctx.fillText('?', x, bottom);
  ctx.restore();
}

/** Name, Zustände und Eigenschaften als Wörter unter der Figur oder dem Kreis */
function drawWords(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, opts: UnitOpts, x: number, top: number): void {
  const words: [string, string][] = [];
  // Voller Name, damit man nicht umrechnen muss (Fabi, Phase 3)
  if (u.kind !== 'tree') words.push([u.name, u.side === 'enemy' ? '#cfcde6' : C.cream]);
  if (u.kindName && u.kindName !== u.name && opts.highlight === u.uid) words.push([u.kindName, '#a9a6c9']);
  for (const [flag, word, color] of STATUS_GLYPHS) {
    if ((u.flags & flag) === 0) continue;
    const w = u.statusWords?.[flag] ?? word;
    if (!words.some(([x]) => x === w)) words.push([w, color]);
  }
  if (opts.showTraits) for (const t of u.traits) words.push([t, '#d8cfe8']);
  ctx.font = `${Math.max(9, s * 1.9)}px ${FONT.body}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  words.forEach(([w, color], i) => {
    ctx.fillStyle = color;
    ctx.fillText(w, x, top + i * s * 2.1);
  });
}

export function drawCursor(ctx: CanvasRenderingContext2D, s: number, c: Cursor, label: string | null): void {
  if (!c.visible) return;
  const x = c.x * s;
  const y = c.y * s;
  ctx.strokeStyle = c.holding ? C.mut : C.cream;
  ctx.lineWidth = Math.max(1.5, s * 0.4);
  ctx.beginPath();
  ctx.arc(x, y, s * 3, 0, Math.PI * 2);
  ctx.moveTo(x - s * 5, y);
  ctx.lineTo(x - s * 3.6, y);
  ctx.moveTo(x + s * 3.6, y);
  ctx.lineTo(x + s * 5, y);
  ctx.moveTo(x, y - s * 5);
  ctx.lineTo(x, y - s * 3.6);
  ctx.moveTo(x, y + s * 3.6);
  ctx.lineTo(x, y + s * 5);
  ctx.stroke();
  if (label) {
    ctx.fillStyle = C.mut;
    ctx.font = `600 ${Math.max(10, s * 2.2)}px ${FONT.body}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(label, x, y - s * 5.5);
  }
}

/** Kurzlebige Effekte aus dem Ereignisprotokoll: Zahlen, Kniffe, Blitze, Böen (K9, K21) */
export function drawEffects(
  ctx: CanvasRenderingContext2D,
  s: number,
  events: BattleEvent[],
  t: number,
  posOf: (uid: number) => { x: number; y: number; r: number; downLabel: string; escapeLabel: string; side: 'friend' | 'enemy' } | null,
  nameOfKniff: (id: string) => string,
  /** Wo stand die Einheit kurz vor diesem Zeitpunkt? Für Sprungspuren */
  posBefore: (uid: number, t: number) => { x: number; y: number } | null = () => null,
  isFlight: (kniff: string) => boolean = () => false,
  /** Phase Kern: Mit Figuren zeigen Sprünge, Flüge und Splitter ihre Bilder statt Spuren und Ringe (K39) */
  view: { figures?: boolean } = {},
): void {
  for (const e of events) {
    if (e.t > t) break;
    const age = t - e.t;
    switch (e.type) {
      case 'damage':
      case 'heal': {
        if (age > 0.9) break;
        const p = posOf(e.to);
        if (!p) break;
        // Kurze Linie vom Angreifer oder Heiler zum Ziel: wer trifft wen.
        // Nusshagel (Fabi, Phase Kern): Eine abgeprallte Nuss kommt vom getroffenen Gegner, nicht vom Eichhörnchen
        const bounced = e.type === 'heal' && e.bounce !== undefined ? posOf(e.bounce) : null;
        const from = bounced ?? (e.from !== null ? posOf(e.from) : null);
        if (bounced && age < 0.3) {
          // die Nuss selbst, kurz vor der Landung noch sichtbar
          const k = Math.min(1, age / 0.12);
          ctx.save();
          ctx.globalAlpha = 1 - age / 0.3;
          ctx.fillStyle = '#c99a5b';
          ctx.beginPath();
          ctx.arc((bounced.x + (p.x - bounced.x) * k) * s, (bounced.y + (p.y - bounced.y) * k) * s, Math.max(2, s * 0.8), 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
        // K40 (Phase Kern): Mit Figuren zeigen Schlag, Wurf und Geschoss, wer wen trifft; die Linie bleibt bei den Kreisen
        if (from && age < 0.3 && !view.figures && (e.type === 'heal' || e.source === 'hit' || e.source === 'kniff')) {
          ctx.globalAlpha = 0.6 * (1 - age / 0.3);
          ctx.strokeStyle = e.type === 'heal' ? C.heal : from.side === 'enemy' ? '#ff8c7a' : C.cream;
          ctx.lineWidth = Math.max(1, s * (e.type === 'damage' && e.source === 'kniff' ? 0.6 : 0.3));
          ctx.beginPath();
          ctx.moveTo(from.x * s, from.y * s);
          ctx.lineTo(p.x * s, p.y * s);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
        const isDamage = e.type === 'damage';
        // Spieltest Phase 4 (Fabi): Splitter deutlicher, kleine Kieselstücke fliegen auseinander
        if (isDamage && e.source === 'splash' && age < 0.45 && !view.figures) {
          ctx.save();
          ctx.globalAlpha = 0.95 * (1 - age / 0.45);
          ctx.fillStyle = '#d9cfbb';
          ctx.strokeStyle = 'rgba(217, 207, 187, 0.8)';
          ctx.lineWidth = s * 0.35;
          ctx.beginPath();
          ctx.arc(p.x * s, p.y * s, (p.r + 1 + age * 10) * s, 0, Math.PI * 2);
          ctx.stroke();
          for (let i = 0; i < 7; i++) {
            const a = (i / 7) * Math.PI * 2 + e.to;
            const d = (p.r + 1 + age * 16) * s;
            ctx.beginPath();
            ctx.arc(p.x * s + Math.cos(a) * d, p.y * s + Math.sin(a) * d, s * 0.7, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }
        // Spieltest Phase 4 (Fabi, Runde 6): Nachwirken (Waschbär, gründlich) sieht anders aus als Splitter: ein
        // sanfter blauer Kringel wie Wasser, keine Kiesel, und die Zahl blau mit Wellenzeichen
        if (isDamage && e.source === 'dot' && age < 0.5) {
          ctx.save();
          ctx.globalAlpha = 0.8 * (1 - age / 0.5);
          ctx.strokeStyle = '#8fd0ee';
          ctx.lineWidth = s * 0.25;
          ctx.setLineDash([s * 0.8, s * 0.6]);
          ctx.beginPath();
          ctx.arc(p.x * s, p.y * s, (p.r + 0.5 + age * 4) * s, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }
        const crit = isDamage && e.crit;
        const big = isDamage && e.source === 'kniff';
        // Fabi (Phase Kern): Hat ein Schild alles abgefangen, steht „geblockt“ statt einer 0
        const allBlocked = isDamage && (e.blocked ?? 0) > 0 && e.amount < 0.5;
        const text = allBlocked ? `${Math.round(e.blocked!)} geblockt` : `${isDamage ? (e.source === 'dot' ? '~' : '') : '+'}${Math.round(e.amount)}${crit ? '!' : ''}${isDamage && e.weakSpot ? '✕' : ''}`;
        ctx.globalAlpha = 1 - age / 0.9;
        // Schaden an Freunden rot, an Gegnern hell; Heilung grün, Gift blassgrün, Nachwirken blau, Volltreffer orange
        ctx.fillStyle = !isDamage ? C.heal : crit ? C.crit : e.source === 'poison' ? '#b7e39f' : e.source === 'dot' ? '#8fd0ee' : e.source === 'splash' ? '#d9cfbb' : e.source === 'lightning' ? C.lightning : p.side === 'friend' ? '#ff8c7a' : C.damage;
        // Kniff-Treffer groß, damit man sieht, dass gerade etwas Besonderes passiert ist (T9)
        ctx.font = `${crit || big ? 800 : 500} ${Math.max(10, s * (big ? 4 : crit ? 3 : 2.2))}px ${FONT.body}`;
        if (big && !crit) ctx.fillStyle = from?.side === 'enemy' ? C.enemyKniff : C.mut;
        if (allBlocked) ctx.fillStyle = '#9fd0f0';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        const jitter = ((e.to * 37 + Math.round(e.t * 20)) % 7) - 3;
        // Hilfsbericht: Zahlen am oberen Rand nicht abschneiden
        ctx.fillText(text, (p.x + jitter * 0.6) * s, Math.max(s * 2.2, (p.y - p.r - 3 - age * 6) * s));
        ctx.globalAlpha = 1;
        break;
      }
      case 'kniff': {
        if (age > 1.3) break;
        const p = posOf(e.unit);
        if (!p) break;
        // T9: Leuchtspur hinter einem Flug-Kniff (Sturzflug)
        if (isFlight(e.kniff) && e.target !== null && age < 0.35 && !view.figures) {
          const q = posOf(e.target);
          if (q) {
            ctx.globalAlpha = 0.9 * (1 - age / 0.35);
            ctx.strokeStyle = C.lightning;
            ctx.lineWidth = s * (1.6 - age * 3);
            ctx.beginPath();
            ctx.moveTo(p.x * s, p.y * s);
            ctx.lineTo(q.x * s, q.y * s);
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        }
        // Ring, der sich ausbreitet: Mut voll, Kniff! Mit Figuren nur, wenn der Kniff kein eigenes Bild hat (Version 3)
        if (age < 0.6 && !(view.figures && hasKniffArt(e.kniff))) {
          ctx.globalAlpha = 1 - age / 0.6;
          ctx.strokeStyle = p.side === 'enemy' ? C.enemyKniff : C.mut;
          ctx.lineWidth = s * (1.2 - age);
          ctx.beginPath();
          ctx.arc(p.x * s, p.y * s, (p.r + 1 + age * 14) * s, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
        ctx.globalAlpha = Math.min(1, 2.2 * (1 - age / 1.3));
        // Freunde gelb, Sturmwesen violett, damit man sofort sieht, wessen Kniff das war (Fabi, Phase 3)
        ctx.fillStyle = p.side === 'enemy' ? C.enemyKniff : C.mut;
        ctx.font = `italic 700 ${Math.max(13, s * 3.2)}px ${FONT.title}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.lineWidth = s * 0.5;
        ctx.strokeStyle = 'rgba(20, 20, 20, 0.7)';
        const ky = Math.max(s * 2.2, (p.y - p.r - 6) * s);
        ctx.strokeText(nameOfKniff(e.kniff) + '!', p.x * s, ky);
        ctx.fillText(nameOfKniff(e.kniff) + '!', p.x * s, ky);
        ctx.globalAlpha = 1;
        break;
      }
      case 'lightningWarning': {
        // P4: Schatten als Vorwarnung
        if (t > e.strikeAt) break;
        const k = 1 - (e.strikeAt - t) / Math.max(0.01, e.strikeAt - e.t);
        ctx.fillStyle = C.shadow;
        ctx.globalAlpha = 0.4 + 0.5 * k;
        ctx.beginPath();
        ctx.arc(e.x * s, e.y * s, e.radius * s, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        break;
      }
      case 'lightning': {
        if (age > 0.35) break;
        ctx.globalAlpha = 1 - age / 0.35;
        ctx.strokeStyle = C.lightning;
        ctx.lineWidth = s * 0.8;
        ctx.beginPath();
        ctx.moveTo(e.x * s + s * 3, 0);
        ctx.lineTo(e.x * s - s * 2, e.y * s * 0.5);
        ctx.lineTo(e.x * s + s * 1.5, e.y * s * 0.6);
        ctx.lineTo(e.x * s, e.y * s);
        ctx.stroke();
        ctx.fillStyle = C.lightning;
        ctx.globalAlpha *= 0.35;
        ctx.beginPath();
        ctx.arc(e.x * s, e.y * s, e.radius * s, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        break;
      }
      case 'gust': {
        if (age > e.duration) break;
        ctx.globalAlpha = 0.45 * Math.min(1, (e.duration - age) * 2);
        ctx.strokeStyle = C.cream;
        ctx.lineWidth = s * 0.3;
        const len = Math.hypot(e.dx, e.dy) || 1;
        for (let i = 0; i < 14; i++) {
          // Striche wandern mit dem Wind übers Feld
          const sx = ((((i * 53) % 150) + 5 + (e.dx / len) * age * 40) % 160 + 160) % 160;
          const sy = ((((i * 29) % 90) + 5 + (e.dy / len) * age * 40) % 100 + 100) % 100;
          ctx.beginPath();
          ctx.moveTo(sx * s, sy * s);
          ctx.lineTo((sx + (e.dx / len) * 12) * s, (sy + (e.dy / len) * 12) * s);
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
        break;
      }
      case 'move': {
        // S8 Gezeter (Fabi, nach Runde 8): Wer vor der Elster erschrickt, bekommt kurz ein Ausrufezeichen, und eine Spur
        // zeigt, wie weit er zurückgewichen ist; die Elster setzt meist gleich nach, darum bleibt die Spur etwas stehen
        if (e.reason === 'scared') {
          if (age > 0.9) break;
          const p = posOf(e.unit);
          if (!p) break;
          ctx.save();
          const from = posBefore(e.unit, e.t);
          if (from && age < 0.7) {
            ctx.globalAlpha = 0.85 * (1 - age / 0.7);
            ctx.strokeStyle = '#cfcde6';
            ctx.lineWidth = s * 0.5;
            ctx.setLineDash([s * 1.2, s * 0.8]);
            ctx.beginPath();
            ctx.moveTo(from.x * s, from.y * s);
            ctx.lineTo(e.x * s, e.y * s);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.arc(e.x * s, e.y * s, s * 0.8, 0, Math.PI * 2);
            ctx.fillStyle = '#cfcde6';
            ctx.fill();
          }
          ctx.globalAlpha = Math.min(1, 2.5 * (1 - age / 0.9));
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
          ctx.lineWidth = Math.max(1, s * 0.4);
          ctx.font = `800 ${Math.max(14, s * 4.5)}px ${FONT.title}`;
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.strokeText('!', (p.x - p.r - 0.6) * s, p.y * s);
          ctx.fillText('!', (p.x - p.r - 0.6) * s, p.y * s);
          ctx.restore();
          break;
        }
        // Sprünge sichtbar machen: gestrichelte Spur vom alten zum neuen Platz; mit Figuren springen sie selbst (K39)
        if (age > 0.8 || e.reason === 'knockback' || view.figures) break;
        const from = posBefore(e.unit, e.t);
        if (!from) break;
        ctx.globalAlpha = 0.8 * (1 - age / 0.8);
        ctx.strokeStyle = e.reason === 'return' ? '#cfcde6' : C.mut;
        ctx.lineWidth = s * 0.5;
        ctx.setLineDash([s * 1.5, s * 1]);
        ctx.beginPath();
        const mx = (from.x + e.x) / 2;
        const my = (from.y + e.y) / 2 - 12;
        ctx.moveTo(from.x * s, from.y * s);
        ctx.quadraticCurveTo(mx * s, my * s, e.x * s, e.y * s);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = ctx.strokeStyle;
        ctx.font = `600 ${Math.max(10, s * 2)}px ${FONT.body}`;
        ctx.textAlign = 'center';
        ctx.fillText(e.reason === 'return' ? 'zurück' : 'Sprung', mx * s, (my + 3) * s);
        ctx.globalAlpha = 1;
        break;
      }
      case 'escape': {
        if (age > 2) break;
        const p = posOf(e.unit);
        if (!p) break;
        ctx.globalAlpha = 1 - age / 2;
        ctx.fillStyle = p.side === 'friend' ? C.heal : '#ffb3a3';
        ctx.font = `italic 700 ${Math.max(11, s * 2.6)}px ${FONT.title}`;
        ctx.textAlign = p.side === 'friend' ? 'left' : 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(p.escapeLabel, (p.x + (p.side === 'friend' ? 2 : -2)) * s, p.y * s);
        ctx.globalAlpha = 1;
        break;
      }
      case 'pushedOut': {
        // K21 (Fabi, Runde 6): über die Kante geschubst, der Freund verlässt den Kampf und wartet am Rand
        if (age > 2) break;
        const p = posOf(e.unit);
        if (!p) break;
        ctx.globalAlpha = 1 - age / 2;
        ctx.fillStyle = '#9fd0f0';
        ctx.font = `italic 700 ${Math.max(11, s * 2.4)}px ${FONT.title}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText('über den Rand!', p.x * s, (p.y - p.r - 1 - age * 3) * s);
        ctx.globalAlpha = 1;
        break;
      }
      case 'down': {
        if (age > 1.6) break;
        const p = posOf(e.unit);
        if (!p) break;
        ctx.globalAlpha = 1 - age / 1.6;
        ctx.fillStyle = C.cream;
        ctx.font = `italic ${Math.max(10, s * 2.2)}px ${FONT.title}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(p.downLabel, p.x * s, p.y * s);
        ctx.globalAlpha = 1;
        break;
      }
      default:
        break;
    }
  }
}

/** Reichweite beim Aufstellen: so weit trifft oder heilt der Freund (Fabi, Phase 3) */
export function drawRange(ctx: CanvasRenderingContext2D, s: number, x: number, y: number, radius: number, range: number, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.12;
  ctx.beginPath();
  // Rand zu Rand: eigener Radius plus Reichweite plus ein typischer Gegnerradius
  ctx.arc(x * s, y * s, (radius + range + 4) * s, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 0.7;
  ctx.setLineDash([s * 1.2, s * 0.8]);
  ctx.lineWidth = Math.max(1, s * 0.3);
  ctx.stroke();
  ctx.restore();
}

function drawGhost(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, highlight: boolean): void {
  ctx.save();
  ctx.globalAlpha = highlight ? 0.8 : 0.4;
  ctx.strokeStyle = u.side === 'enemy' ? '#b9b7e0' : C.cream;
  ctx.setLineDash([s * 0.8, s * 0.8]);
  ctx.lineWidth = Math.max(1, s * 0.3);
  ctx.beginPath();
  ctx.arc(u.x * s, u.y * s, u.radius * s, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = ctx.strokeStyle;
  ctx.font = `italic ${Math.max(9, s * 1.8)}px ${FONT.title}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(u.name, u.x * s, (u.y + u.radius + 0.6) * s);
  ctx.fillText(u.goneLabel ?? '', u.x * s, (u.y + u.radius + 2.7) * s);
  ctx.restore();
}

/** Fabi, Phase 3: Gegner anklicken zeigt, wen er zuerst angreifen würde */
export function drawTargetArrow(ctx: CanvasRenderingContext2D, s: number, from: DrawUnit, to: DrawUnit, color: string = C.enemyKniff): void {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const d = Math.hypot(dx, dy) || 1;
  const sx = from.x + (dx / d) * (from.radius + 1);
  const sy = from.y + (dy / d) * (from.radius + 1);
  const ex = to.x - (dx / d) * (to.radius + 1.5);
  const ey = to.y - (dy / d) * (to.radius + 1.5);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = Math.max(1.5, s * 0.5);
  ctx.setLineDash([s * 1.5, s * 1]);
  ctx.beginPath();
  ctx.moveTo(sx * s, sy * s);
  ctx.lineTo(ex * s, ey * s);
  ctx.stroke();
  ctx.setLineDash([]);
  const a = Math.atan2(dy, dx);
  ctx.beginPath();
  ctx.moveTo(ex * s, ey * s);
  ctx.lineTo((ex - Math.cos(a - 0.4) * 3) * s, (ey - Math.sin(a - 0.4) * 3) * s);
  ctx.lineTo((ex - Math.cos(a + 0.4) * 3) * s, (ey - Math.sin(a + 0.4) * 3) * s);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * K27 (Fabi, Spieltest Phase 4): Marschrichtung als dicker Pfeil mit Griff am Ende; ohne Marsch nur ein kleiner Griff
 * neben dem Freund, an dem man ziehen kann. Gegner zeigen ihren Marsch als schmalen Pfeil ohne Griff.
 */
export function drawMarch(ctx: CanvasRenderingContext2D, s: number, x: number, y: number, radius: number, to: { x: number; y: number } | null, opts: { color: string; knob: boolean; reach?: number }): void {
  ctx.save();
  if (opts.reach !== undefined && opts.knob) {
    // Wie weit er kommt: blasser Kreis
    ctx.strokeStyle = opts.color;
    ctx.globalAlpha = 0.18;
    ctx.lineWidth = Math.max(1, s * 0.3);
    ctx.setLineDash([s * 1, s * 1.2]);
    ctx.beginPath();
    ctx.arc(x * s, y * s, opts.reach * s, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }
  if (to) {
    const dx = to.x - x;
    const dy = to.y - y;
    const d = Math.hypot(dx, dy) || 1;
    const sx = x + (dx / d) * (radius + 0.8);
    const sy = y + (dy / d) * (radius + 0.8);
    ctx.strokeStyle = opts.color;
    ctx.fillStyle = opts.color;
    ctx.globalAlpha = opts.knob ? 0.9 : 0.7;
    ctx.lineWidth = Math.max(opts.knob ? 2.5 : 1.5, s * (opts.knob ? 0.8 : 0.45));
    ctx.beginPath();
    ctx.moveTo(sx * s, sy * s);
    ctx.lineTo(to.x * s, to.y * s);
    ctx.stroke();
    const a = Math.atan2(dy, dx);
    const h = opts.knob ? 3.4 : 2.6;
    ctx.beginPath();
    ctx.moveTo((to.x + Math.cos(a) * 1.2) * s, (to.y + Math.sin(a) * 1.2) * s);
    ctx.lineTo((to.x - Math.cos(a - 0.45) * h) * s, (to.y - Math.sin(a - 0.45) * h) * s);
    ctx.lineTo((to.x - Math.cos(a + 0.45) * h) * s, (to.y - Math.sin(a + 0.45) * h) * s);
    ctx.closePath();
    ctx.fill();
  }
  if (opts.knob) {
    const k = to ?? { x: x + radius + 3, y };
    ctx.globalAlpha = to ? 0.95 : 0.55;
    ctx.fillStyle = '#fdf6e3';
    ctx.strokeStyle = opts.color;
    ctx.lineWidth = Math.max(1, s * 0.35);
    ctx.beginPath();
    ctx.arc(k.x * s, k.y * s, Math.max(4, s * 1.1), 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    if (!to) {
      ctx.fillStyle = '#2a2118';
      ctx.font = `700 ${Math.max(8, s * 1.6)}px ${FONT.body}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('›', k.x * s, k.y * s + 0.5);
    }
  }
  ctx.restore();
}
